import axios from 'axios';
import {
  createAuthError,
  isAuthFailed,
  isUsableSession,
  loadSession,
  recoverAuth,
} from '../services/auth';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

const TOKEN_REFRESH_WINDOW_SECONDS = 300;

const rejectAuth = async () => {
  await recoverAuth();
  return Promise.reject(createAuthError());
};

// Request interceptor to add auth headers and validate tokens
api.interceptors.request.use(
  async (config) => {
    if (
      config.baseURL !== import.meta.env.VITE_API_URL ||
      config.headers.Authorization
    ) {
      return config;
    }

    if (isAuthFailed()) {
      return rejectAuth();
    }

    let session = await loadSession(false);
    const now = Date.now() / 1000;
    if (
      isUsableSession(session) &&
      session.expires - now < TOKEN_REFRESH_WINDOW_SECONDS
    ) {
      session = await loadSession(true);
    }

    if (!isUsableSession(session)) {
      if (isAuthFailed()) {
        return rejectAuth();
      }
      return Promise.reject(new Error('Token refresh unavailable'));
    }

    config.headers.Authorization = session.credentials.idToken;
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor to handle 401 errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.isAuthError || isAuthFailed()) {
      return Promise.reject(error?.isAuthError ? error : createAuthError());
    }

    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      const session = await loadSession(true, { bypass_cooldown: true });
      const next_token = session?.credentials?.idToken;
      const current_token = originalRequest.headers?.Authorization;
      if (
        isUsableSession(session) &&
        next_token &&
        next_token !== current_token
      ) {
        originalRequest.headers.Authorization = next_token;
        return api(originalRequest);
      }
      if (isAuthFailed()) {
        return rejectAuth();
      }
      return Promise.reject(error);
    }

    // Add rate limiting for repeated errors to prevent infinite loops
    if (
      error.response?.status >= 500 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthFailed()
    ) {
      originalRequest._retry = true;
      // Wait 1 second before retrying server errors
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return api(originalRequest);
    }

    return Promise.reject(error);
  },
);

export default api;
